import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, UtensilsCrossed, Clock, Truck, Star, BadgeCheck, Leaf, Quote, Reply, Tag, Percent, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import MenuItemCard from '../components/MenuItemCard';
import HeroCarousel from '../components/HeroCarousel';
import RatingModal from '../components/RatingModal';
import AnimatedLabel from '../components/AnimatedLabel';
import { apiRequest } from '../lib/utils';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [popularItems, setPopularItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [promotionsData, setPromotionsData] = useState({ promotions: [], promo_codes: [] });
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);

  // Get localized category name based on current language
  const getLocalizedCategoryName = (category) => {
    const lang = i18n.language;
    if (lang === 'it' && category.name_it) return category.name_it;
    if (lang === 'de' && category.name_de) return category.name_de;
    if (lang === 'fr' && category.name_fr) return category.name_fr;
    if (lang === 'ar' && category.name_ar) return category.name_ar;
    return category.name; // Default to English
  };

  // Get localized promotion title based on current language
  const getLocalizedPromoTitle = (promo) => {
    const lang = i18n.language;
    if (lang === 'it' && promo.title_it) return promo.title_it;
    if (lang === 'de' && promo.title_de) return promo.title_de;
    if (lang === 'fr' && promo.title_fr) return promo.title_fr;
    if (lang === 'ar' && promo.title_ar) return promo.title_ar;
    return promo.title;
  };

  // Get localized promotion description based on current language
  const getLocalizedPromoDesc = (promo) => {
    const lang = i18n.language;
    if (lang === 'it' && promo.description_it) return promo.description_it;
    if (lang === 'de' && promo.description_de) return promo.description_de;
    if (lang === 'fr' && promo.description_fr) return promo.description_fr;
    if (lang === 'ar' && promo.description_ar) return promo.description_ar;
    return promo.description;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [items, cats, revs, promos] = await Promise.all([
          apiRequest('/menu/popular'),
          apiRequest('/categories'),
          apiRequest('/reviews'),
          apiRequest('/promotions/all')
        ]);
        setPopularItems(items);
        setCategories(cats);
        setReviews(revs);
        const promosData = promos || { promotions: [], promo_codes: [] };
        setPromotionsData(promosData);

        // Auto-open fullscreen promo modal on first visit if there are active promotions
        const hasPromos = (promosData.promotions?.length || 0) + (promosData.promo_codes?.length || 0) > 0;
        if (hasPromos) {
          const closedPromos = JSON.parse(localStorage.getItem('closedPromoModal') || '[]');
          const promoIds = [
            ...promosData.promotions.map(p => p.id),
            ...promosData.promo_codes.map(c => c.id)
          ];
          const hasNew = promoIds.some(id => !closedPromos.includes(id));
          if (hasNew) {
            setTimeout(() => setPromoModalOpen(true), 800);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Listen for promo modal open event from navbar
  useEffect(() => {
    const handler = () => setPromoModalOpen(true);
    window.addEventListener('open-promo-modal', handler);
    return () => window.removeEventListener('open-promo-modal', handler);
  }, []);

  const features = [
    {
      icon: BadgeCheck,
      title: t('features.halal'),
      description: t('features.halalDesc')
    },
    {
      icon: UtensilsCrossed,
      title: t('features.freshIngredients'),
      description: t('features.freshIngredientsDesc')
    },
    {
      icon: Clock,
      title: t('features.fastDelivery'),
      description: t('features.fastDeliveryDesc')
    },
    {
      icon: Star,
      title: t('features.openLate'),
      description: t('features.openLateDesc')
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Tasty Bites | First 100% Halal Food Takeaway in Lugano</title>
        <meta name="description" content="Tasty Bites - First 100% Halal food takeaway in Lugano. Pakistani, Indian & fast food. Delivery & pickup." />
        <link rel="canonical" href="https://www.tastybiteslugano.ch/" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-80px)] flex items-center overflow-hidden">
        {/* Automatic Image Carousel */}
        <HeroCarousel images={categories.filter(c => c.image).map(c => c.image)} interval={4000} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10">
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFC107]/10 text-[#FFC107] text-sm md:text-base font-medium uppercase tracking-wider">
                {t('hero.cuisineTypes')}
              </span>
              <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-green-500/20 text-green-400 text-sm md:text-base font-bold uppercase tracking-wider border border-green-500/30">
                <BadgeCheck className="h-4 w-4" />
                {t('features.halal')}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-tight text-white mb-6 font-['Barlow_Condensed']">
              {t('hero.title1')}<br />
              <span className="text-[#FFC107]">{t('hero.title2')}</span><br />
              {t('hero.title3')}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 mb-8 max-w-xl leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/menu">
                <Button className="group h-12 px-8 text-base bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wider logo-fx" data-testid="order-now-button">
                  <AnimatedLabel text={t('hero.orderNow')} />
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/menu">
                <Button variant="outline" className="group h-12 px-8 text-base border-zinc-700 hover:bg-[#25252E] font-bold uppercase tracking-wide logo-fx" data-testid="view-menu-button">
                  <AnimatedLabel text={t('hero.viewMenu')} hoverClass="group-hover:text-[#FFC107]" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-[#141417]/60 border-y border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFC107]/10 mb-4">
                  <feature.icon className="h-7 w-7 text-[#FFC107]" />
                </div>
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
          {/* Notices (TEMPORARY: delivery zone notice hidden while delivery is suspended) */}
          {false && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/10 border border-green-500/30 rounded-full">
              <Truck className="h-5 w-5 text-green-500" />
              <span className="text-green-500 font-medium text-sm">{t('features.freeDeliveryNotice')}</span>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Rating Modal */}
      <RatingModal isOpen={isRatingModalOpen} onClose={() => setIsRatingModalOpen(false)} />

      {/* Fullscreen Promotions Modal */}
      {promoModalOpen && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm overflow-y-auto" data-testid="promo-fullscreen-modal">
          <div className="min-h-screen px-4 py-8">
            <div className="flex items-center justify-between max-w-3xl mx-auto mb-8">
              <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
                {t('sections.promotions')}
              </h2>
              <button
                onClick={() => {
                  setPromoModalOpen(false);
                  const promoIds = [
                    ...promotionsData.promotions.map(p => p.id),
                    ...promotionsData.promo_codes.map(c => c.id)
                  ];
                  localStorage.setItem('closedPromoModal', JSON.stringify(promoIds));
                }}
                className="p-2 rounded-full bg-[#1C1C22] hover:bg-zinc-700 transition-colors"
                data-testid="promo-modal-close"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {/* Promotion banners */}
              {promotionsData.promotions.map(promo => (
                <Link
                  key={promo.id}
                  to={promo.button_link || '/menu'}
                  onClick={() => setPromoModalOpen(false)}
                  className="block group overflow-hidden rounded-xl border border-[#27272A] hover:border-[#FFC107]/40 transition-all duration-300"
                >
                  {promo.image && (
                    <img src={promo.image} alt={getLocalizedPromoTitle(promo)} className="w-full h-48 md:h-64 object-cover" />
                  )}
                  <div className="p-5 bg-[#141417]/80">
                    <h3 className="font-bold text-white text-xl">{getLocalizedPromoTitle(promo)}</h3>
                    <p className="text-sm text-zinc-400 mt-2">{getLocalizedPromoDesc(promo)}</p>
                    <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-[#FFC107] group-hover:underline">
                      {promo.button_text || t('sections.orderNow')} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}

              {/* Promo codes */}
              {promotionsData.promo_codes.map(code => (
                <div
                  key={code.id}
                  className="overflow-hidden rounded-xl border border-dashed border-[#FFC107]/40 bg-[#FFC107]/5 p-6"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Percent className="h-5 w-5 text-[#FFC107]" />
                    <span className="text-xs font-medium text-[#FFC107] uppercase tracking-wider">{t('sections.promoCode')}</span>
                  </div>
                  <div className="bg-[#141417] border border-zinc-700 rounded-lg px-6 py-4 text-center mb-4">
                    <span className="text-2xl font-bold text-white tracking-widest font-mono">{code.code}</span>
                  </div>
                  <p className="text-base text-white font-medium">
                    {code.discount_type === 'percentage'
                      ? `${code.discount_value}% ${t('sections.discount')}`
                      : `CHF ${code.discount_value} ${t('sections.discount')}`}
                  </p>
                  {code.min_order_amount > 0 && (
                    <p className="text-sm text-zinc-400 mt-1">{t('sections.minOrder')}: CHF {code.min_order_amount}</p>
                  )}
                  {code.expires_at && (
                    <p className="text-xs text-zinc-500 mt-3">{t('sections.validUntil')}: {new Date(code.expires_at).toLocaleDateString()}</p>
                  )}
                </div>
              ))}

              {promotionsData.promotions.length === 0 && promotionsData.promo_codes.length === 0 && (
                <div className="text-center py-16">
                  <Tag className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-500 text-lg">{t('sections.noPromotions')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Promotions Section */}
      {(promotionsData.promotions.length > 0 || promotionsData.promo_codes.length > 0) && (
        <section className="py-12 md:py-16" data-testid="promotions-section" id="promotions-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                {t('sections.promotions').split(' ').slice(0, -1).join(' ')} <span className="text-[#FFC107]">{t('sections.promotions').split(' ').slice(-1)}</span>
              </h2>
              <p className="text-zinc-400 text-sm md:text-base">{t('sections.promotionsDesc')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Promotion banners */}
              {promotionsData.promotions.map(promo => (
                <div
                  key={promo.id}
                  onClick={() => setPromoModalOpen(true)}
                  className="group relative overflow-hidden rounded-xl border border-[#27272A] hover:border-[#FFC107]/40 transition-all duration-300 cursor-pointer"
                  data-testid={`promo-banner-${promo.id}`}
                >
                  {promo.image && (
                    <img src={promo.image} alt={getLocalizedPromoTitle(promo)} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-4 bg-[#141417]/80">
                    <h3 className="font-bold text-white text-lg">{getLocalizedPromoTitle(promo)}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{getLocalizedPromoDesc(promo)}</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[#FFC107] group-hover:underline">
                      {promo.button_text || t('sections.orderNow')} <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              ))}

              {/* Promo codes */}
              {promotionsData.promo_codes.map(code => (
                <div
                  key={code.id}
                  className="relative overflow-hidden rounded-xl border border-dashed border-[#FFC107]/40 bg-[#FFC107]/5 p-5 flex flex-col justify-between"
                  data-testid={`promo-code-${code.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Percent className="h-5 w-5 text-[#FFC107]" />
                      <span className="text-xs font-medium text-[#FFC107] uppercase tracking-wider">{t('sections.promoCode')}</span>
                    </div>
                    <div className="bg-[#141417] border border-zinc-700 rounded-lg px-4 py-2.5 text-center mb-3">
                      <span className="text-xl font-bold text-white tracking-widest font-mono">{code.code}</span>
                    </div>
                    <p className="text-sm text-white font-medium">
                      {code.discount_type === 'percentage'
                        ? `${code.discount_value}% ${t('sections.discount')}`
                        : `CHF ${code.discount_value} ${t('sections.discount')}`}
                    </p>
                    {code.min_order_amount > 0 && (
                      <p className="text-xs text-zinc-400 mt-1">{t('sections.minOrder')}: CHF {code.min_order_amount}</p>
                    )}
                  </div>
                  {code.expires_at && (
                    <p className="text-xs text-zinc-500 mt-3">
                      {t('sections.validUntil')}: {new Date(code.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              {t('sections.browseByCategory').split(' ').slice(0, -1).join(' ')} <span className="text-[#FFC107]">{t('sections.browseByCategory').split(' ').slice(-1)}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => {
              const localizedName = getLocalizedCategoryName(category);
              const localizedDesc = (() => {
                const lang = i18n.language;
                if (lang === 'it' && category.description_it) return category.description_it;
                if (lang === 'de' && category.description_de) return category.description_de;
                if (lang === 'fr' && category.description_fr) return category.description_fr;
                if (lang === 'ar' && category.description_ar) return category.description_ar;
                return category.description;
              })();
              
              return (
                <Link
                  key={category.id}
                  to={`/menu?category=${category.id}`}
                  className="group relative aspect-square overflow-hidden rounded-xl"
                  style={{ animationDelay: `${index * 80}ms` }}
                  data-testid={`category-${category.id}`}
                >
                  <img
                    src={category.image}
                    alt={localizedName}
                    className="w-full h-full object-cover object-center transition-all duration-700 ease-out group-hover:scale-110 group-hover:brightness-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent transition-all duration-500 group-hover:from-black/70 group-hover:via-black/10" />
                  <div className="absolute inset-0 border-2 border-transparent rounded-xl transition-all duration-500 group-hover:border-[#FFC107]/50 group-hover:shadow-[inset_0_0_30px_rgba(255,193,7,0.1)]" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transition-all duration-500 group-hover:translate-y-[-4px]">
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] transition-colors duration-300 group-hover:text-[#FFC107]">
                      {localizedName}
                    </h3>
                    <p className="text-sm text-zinc-300 hidden md:block transition-all duration-500 opacity-70 group-hover:opacity-100">{localizedDesc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Items */}
      {popularItems.length > 0 && (
        <section className="py-16 md:py-24 bg-[#141417]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-2 font-['Barlow_Condensed']">
                  {t('sections.popularDishes').split(' ')[0]} <span className="text-[#FFC107]">{t('sections.popularDishes').split(' ').slice(1).join(' ')}</span>
                </h2>
              </div>
              <Link to="/menu" className="hidden md:block">
                <Button variant="outline" className="border-zinc-700 hover:bg-[#25252E]" data-testid="view-all-button">
                  {t('sections.viewAll')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {popularItems.slice(0, 8).map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>

            <div className="mt-8 text-center md:hidden">
              <Link to="/menu">
                <Button variant="outline" className="border-zinc-700 hover:bg-[#25252E]">
                  View All Menu
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Customer Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                {t('sections.whatCustomersSay')} <span className="text-[#FFC107]">{t('sections.customersSay')}</span>
              </h2>
              <p className="text-zinc-400 max-w-xl mx-auto">
                {t('sections.realReviews')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((review) => (
                <div
                  key={review.id}
                  className="bg-[#141417]/60 border border-[#27272A] rounded-xl p-6 hover:border-[#FFC107]/30 transition-colors"
                  data-testid={`home-review-${review.id}`}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= review.rating
                            ? 'text-[#FFC107] fill-[#FFC107]'
                            : 'text-zinc-600'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {review.feedback && (
                    <div className="relative mb-4">
                      <Quote className="absolute -top-2 -left-2 h-6 w-6 text-[#FFC107]/20" />
                      <p className="text-zinc-300 text-sm pl-4">{review.feedback}</p>
                    </div>
                  )}
                  
                  {review.admin_reply && (
                    <div className="mt-3 p-3 bg-[#FFC107]/10 rounded-lg border border-[#FFC107]/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Reply className="h-3 w-3 text-[#FFC107]" />
                        <span className="text-xs font-semibold text-[#FFC107]">{t('reviews.ownerReply')}</span>
                      </div>
                      <p className="text-zinc-400 text-xs">{review.admin_reply}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#27272A]">
                    <span className="font-semibold text-white text-sm">{review.name}</span>
                    <span className="text-xs text-zinc-500">
                      {new Date(review.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button
                variant="outline"
                className="border-zinc-700 hover:bg-[#25252E]"
                onClick={() => setIsRatingModalOpen(true)}
                data-testid="leave-review-button"
              >
                <Star className="mr-2 h-4 w-4" />
                {t('sections.leaveReview')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-[#141417] border border-[#27272A]">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: `url(https://customer-assets.emergentagent.com/job_pakindi-eats/artifacts/rmebub98_Chicken-Biryani.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 via-zinc-900/80 to-transparent" />
            
            <div className="relative p-8 md:p-16">
              <div className="max-w-xl">
                <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                  {t('cta.hungry')} <span className="text-[#FFC107]">{t('cta.weDeliver')}</span>
                </h2>
                <p className="text-zinc-300 mb-8">
                  {t('cta.ctaDescription')}
                </p>
                <Link to="/menu">
                  <Button className="group h-12 px-8 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wider logo-fx" data-testid="start-order-button">
                    <AnimatedLabel text={t('cta.startOrder')} />
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Share Section */}
      <section className="py-12 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              {t('sections.shareWithFriends')} <span className="text-[#FFC107]">{t('sections.tastyBites')}</span> {t('sections.withFriends')}
            </h3>
            <p className="text-zinc-400 mb-6 text-sm">
              {t('sections.followUs')}
            </p>
            <div className="flex justify-center gap-4">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/tastybiteslugano"
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-[#141417] border border-[#27272A] rounded-xl hover:border-[#E4405F] hover:bg-[#E4405F]/10 transition-all group"
                data-testid="share-instagram"
              >
                <svg className="h-6 w-6 text-zinc-400 group-hover:text-[#E4405F] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>

              {/* Facebook */}
              <a
                href="https://www.facebook.com/profile.php?id=61588429354979"
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-[#141417] border border-[#27272A] rounded-xl hover:border-[#1877F2] hover:bg-[#1877F2]/10 transition-all group"
                data-testid="share-facebook"
              >
                <svg className="h-6 w-6 text-zinc-400 group-hover:text-[#1877F2] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/41765626183"
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-[#141417] border border-[#27272A] rounded-xl hover:border-[#25D366] hover:bg-[#25D366]/10 transition-all group"
                data-testid="share-whatsapp"
              >
                <svg className="h-6 w-6 text-zinc-400 group-hover:text-[#25D366] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
